import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { useT } from "../context/LangContext";
import { DONATION_TIERS } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMPACT_ICONS = [Heart, ShieldCheck];

export default function Sostienici() {
  const { t } = useT();
  const c = t.donatePage;
  const [selected, setSelected] = useState("tier_25");
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const renewal = params.get("scopo") === "rinnovo";

  const handleDonate = async () => {
    setLoading(true);
    try {
      const payload = { origin_url: window.location.origin, project: renewal ? "Rinnovo quota associativa" : "Sostegno generale alle attività" };
      if (custom) {
        const val = parseFloat(custom.replace(",", "."));
        if (isNaN(val) || val < 2) {
          toast.error(c.errAmount);
          setLoading(false);
          return;
        }
        payload.custom_amount = val;
      } else {
        payload.tier = selected;
      }
      const { data } = await axios.post(`${API}/donations/checkout`, payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(c.errStart);
        setLoading(false);
      }
    } catch (e) {
      toast.error(c.errGeneric);
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero kicker={c.heroKicker} titleLines={c.heroTitleLines} intro={c.heroIntro} />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-12 md:py-16 grid gap-10 md:grid-cols-12" data-testid="donation-section">
        <Reveal className="md:col-span-7">
          <div className="rounded-[2rem] border border-border bg-surface p-7 md:p-10">
            <h2 className="font-display font-extrabold tracking-tighter text-2xl md:text-3xl text-ink mb-2">{c.chooseTitle}</h2>
            <p className="text-stone mb-8">{c.chooseSub}</p>
            {renewal && (
              <div data-testid="renewal-banner" className="mb-6 rounded-2xl border border-forest/30 bg-forest/5 px-5 py-4 text-sm text-ink">
                <span className="font-semibold">{c.renewalTitle}</span> {c.renewalBody}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {DONATION_TIERS.map((tier, i) => {
                const active = selected === tier.id && !custom;
                return (
                  <button
                    key={tier.id}
                    data-testid={`tier-${tier.amount}`}
                    onClick={() => { setSelected(tier.id); setCustom(""); }}
                    className={`text-left rounded-2xl border p-5 transition-colors duration-200 ${active ? "border-terracotta bg-terracotta/5" : "border-border hover:border-ink/30"}`}
                  >
                    <div className="font-display font-extrabold tracking-tighter text-3xl text-ink">{tier.amount}€</div>
                    <div className="mt-1 text-xs text-stone">{c.tiers[i].note}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <label className="font-display font-bold tracking-widest text-xs text-forest">{c.customLabel}</label>
              <div className="mt-3 flex items-center rounded-2xl border border-border focus-within:border-terracotta transition-colors duration-200 overflow-hidden">
                <span className="px-4 text-xl text-stone">€</span>
                <input
                  data-testid="custom-amount-input"
                  type="text"
                  inputMode="decimal"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder={c.customPlaceholder}
                  className="w-full bg-transparent py-4 pr-4 text-lg text-ink outline-none placeholder:text-stone/50"
                />
              </div>
            </div>

            <button
              data-testid="donate-submit-btn"
              onClick={handleDonate}
              disabled={loading}
              className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark disabled:opacity-60"
            >
              {loading ? c.submitLoading : c.submitIdle}
              {!loading && <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />}
            </button>
            <p className="mt-4 text-center text-xs text-stone">{c.redirectNote}</p>
          </div>
        </Reveal>

        <div className="md:col-span-5 space-y-6">
          {c.impact.map((it, i) => {
            const Icon = IMPACT_ICONS[i];
            return (
              <Reveal key={it.title} delay={i * 0.1}>
                <div className="rounded-2xl border border-border bg-sand p-7">
                  <Icon size={34} weight="duotone" className="text-terracotta mb-4" />
                  <h3 className="font-display font-bold tracking-tight text-lg text-ink mb-2">{it.title}</h3>
                  <p className="text-sm text-stone leading-relaxed">{it.body}</p>
                </div>
              </Reveal>
            );
          })}
          <Reveal delay={0.2}>
            <div className="rounded-2xl bg-forest text-bone p-7">
              <p className="font-serif italic text-2xl leading-snug">{c.quote}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
